import React from "react";
import { Badge, Box, Center, Group, Text, ThemeIcon, Tooltip } from "@mantine/core";
import {
  IconAlertTriangle,
  IconCircleCheck,
  IconCircleX,
} from "@tabler/icons";
import moment from "moment";
import { useCellRender } from "../hooks/useCellRender";
import {
  formatCandidateDebitNoteCodes,
  getMatchStatusColor,
  getMatchStatusLabel,
  getMatchStatusTooltip,
  getOverdueColor,
  getRowValidationStatus,
  getRowValidationTooltipKey,
  isRowAmountMatched,
} from "../pages/gi/giSettlementCrossCheckUtils";

const VALIDATION_ICON = {
  ok: { Icon: IconCircleCheck, color: "green" },
  warning: { Icon: IconAlertTriangle, color: "orange" },
  error: { Icon: IconCircleX, color: "red" },
};

const ValidationIcon = ({ row, t }) => {
  const status = getRowValidationStatus(row);
  const tooltipKey = getRowValidationTooltipKey(row);
  const defaultLabels = {
    "giSettlement.crossCheck.check.matched":
      "Policy found in settlement and amount matches",
    "giSettlement.crossCheck.check.amountMismatch":
      "Policy found but amount does not match",
    "giSettlement.crossCheck.check.notFound":
      "No matching debit note found in the system for this insurer",
    "giSettlement.crossCheck.check.notInSettlement":
      "Debit note exists but is not in this settlement batch",
    "giSettlement.crossCheck.check.ambiguous":
      "Multiple matching debit notes found — cannot auto-map",
    "giSettlement.crossCheck.check.policyMismatch":
      "Matched by DN code but policy number does not match",
  };
  const { Icon, color } = VALIDATION_ICON[status] || VALIDATION_ICON.error;

  return (
    <Tooltip label={t(tooltipKey, defaultLabels[tooltipKey])} withArrow>
      <Center inline>
        <ThemeIcon color={color} variant="light" size="sm" radius="xl">
          <Icon size={14} stroke={1.75} />
        </ThemeIcon>
      </Center>
    </Tooltip>
  );
};

const GISettlementCrossCheckRow = ({
  row,
  index,
  t,
  hideInsurer = false,
  showDebitNoteDetails = false,
}) => {
  const [cellRender] = useCellRender();
  const statementDate = row.statementDate
    ? moment(row.statementDate).format("YYYY-MM-DD")
    : "—";
  const insurerDisplay =
    row.insurerCode || row.insurerLabel || row.insuredName || "—";

  return (
    <tr>
      <td style={{ width: 40, minWidth: 40 }}>
        <Text size="xs" fw={600} ta="center">
          {(index ?? 0) + 1}
        </Text>
      </td>
      <td>
        <Text size="xs" fw={500} lineClamp={1}>
          {row.policyNumber}
        </Text>
        {row.insurerRef ? (
          <Text size="xs" c="dimmed" lineClamp={1}>
            {row.insurerRef}
          </Text>
        ) : null}
        {row.dnType || row.dnCode ? (
          <Text size="xs" c="dimmed" lineClamp={1}>
            {[
              row.dnType
                ? t(`giSettlement.crossCheck.dnType.${row.dnType}`, row.dnType)
                : null,
              row.dnCode || null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </Text>
        ) : null}
      </td>
      <td>
        <Text size="xs" lineClamp={1}>
          {row.insurerStatementCode || "—"}
        </Text>
        <Text size="xs" c="dimmed">
          {statementDate}
        </Text>
      </td>
      {!hideInsurer ? (
        <td>
          <Tooltip label={row.insurerLabel || insurerDisplay} withArrow>
            <Text size="xs" lineClamp={1}>
              {insurerDisplay}
            </Text>
          </Tooltip>
        </td>
      ) : null}
      {showDebitNoteDetails ? (
        <td>
          <Text size="xs" lineClamp={2}>
            {row.consultantsLabel || "—"}
          </Text>
        </td>
      ) : null}
      {showDebitNoteDetails ? (
        <td>
          <Text size="xs" lineClamp={1}>
            {row.clientName || "—"}
          </Text>
          {row.productTypeLabel || row.productTypeCode ? (
            <Text size="xs" c="dimmed" lineClamp={1}>
              {row.productTypeLabel || row.productTypeCode}
            </Text>
          ) : null}
        </td>
      ) : null}
      <td>
        <Text size="xs">
          {row.netPremium != null
            ? `${t("giSettlement.crossCheck.amount.statement", "Stmt")} ${cellRender.Currency(row.netPremium, row.currency)}`
            : "—"}
        </Text>
        <Text
          size="xs"
          c={isRowAmountMatched(row) ? "dimmed" : "red"}
          lineClamp={1}
        >
          {row.systemSettleAmount != null
            ? `${t("giSettlement.crossCheck.amount.system", "Sys")} ${cellRender.Currency(row.systemSettleAmount, row.currency)}`
            : "—"}
        </Text>
      </td>
      <td>
        <Text size="xs" fw={500} color={getOverdueColor(row.overdueDays)}>
          {row.overdueDays ?? 0}
        </Text>
      </td>
      <td>
        <Tooltip
          label={getMatchStatusTooltip(row, t, cellRender.DebitNotePaymentStatus)}
          withArrow
          multiline
          maw={320}
        >
          <Box component="span" sx={{ display: "inline-block", cursor: "help" }}>
            <Badge size="xs" color={getMatchStatusColor(row.matchStatus)}>
              {getMatchStatusLabel(row.matchStatus, t)}
            </Badge>
          </Box>
        </Tooltip>
        {row.matchedDebitNoteCode ? (
          <Group spacing={4} noWrap mt={2}>
            <Text size="xs" c="dimmed" lineClamp={1} component="span">
              {row.matchedDebitNoteCode}
            </Text>
            {row.matchStatus === "NOT_IN_SETTLEMENT" && row.systemPaymentStatus ? (
              <>
                <Text size="xs" c="dimmed" component="span">
                  ·
                </Text>
                {cellRender.DebitNotePaymentStatus(row.systemPaymentStatus)}
              </>
            ) : null}
          </Group>
        ) : null}
        {row.matchStatus === "AMBIGUOUS" && row.candidateDebitNotes?.length ? (
          <Text size="xs" c="orange" lineClamp={2} mt={2}>
            {formatCandidateDebitNoteCodes(row.candidateDebitNotes)}
          </Text>
        ) : null}
      </td>
      <td style={{ width: 44, minWidth: 44 }}>
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <ValidationIcon row={row} t={t} />
        </Box>
      </td>
    </tr>
  );
};

export default GISettlementCrossCheckRow;
